import { useEffect, useRef, useState } from "react";
import { Loader2, FileText } from "lucide-react";
import { Trans } from "@lingui/macro";
import * as pdfjsLib from "pdfjs-dist";
import { renderAsync as renderDocx } from "docx-preview";

// Configure the pdf.js worker. `new URL(..., import.meta.url)` lets Vite resolve
// the worker shipped with the installed pdfjs-dist (no CDN, version-matched).
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

// Side files pdf.js needs at runtime, served from /pdfjs/* (copied at build time
// by vite-plugin-static-copy, version-matched to pdfjs-dist). wasmUrl lets it
// decode JBIG2/JPEG2000 images — without it, scanned PDFs (e.g. multi-patient
// bundles) drop those images and look like they are missing text. Trailing slash
// is required by the API; BASE_URL keeps it correct under a non-root deploy base.
const PDF_ASSET_BASE = `${import.meta.env.BASE_URL}pdfjs/`;
const PDF_DOCUMENT_OPTIONS = {
  wasmUrl: `${PDF_ASSET_BASE}wasm/`,
  cMapUrl: `${PDF_ASSET_BASE}cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `${PDF_ASSET_BASE}standard_fonts/`,
} as const;

interface ViewOnlyDocumentProps {
  /** Same-origin blob URL of the already-fetched document bytes. */
  blobUrl: string;
  mimeType: string | null;
  title: string;
  /** The server's text of a Word 97-2003 file; null when it has none to give. */
  loadLegacyWordText?: () => Promise<string | null>;
}

// A Word 97-2003 file opens with these bytes whatever its name — Calimed serves it as .bin.
const CFB_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

function isLegacyWord(bytes: Uint8Array): boolean {
  return CFB_MAGIC.every((byte, i) => bytes[i] === byte);
}

type Kind =
  | "pdf"
  | "image"
  | "video"
  | "html"
  | "docx"
  | "text"
  | "unsupported";

/** Decode text/HTML bytes honouring the declared charset, else UTF-8, else
 *  Windows-1252 (Latin-1). French clinical exports (e.g. Calimed) are frequently
 *  Latin-1; decoding them as UTF-8 yields replacement chars (�) for accents and
 *  the non-breaking space 0xA0. */
function decodeTextBytes(bytes: Uint8Array): string {
  // Windows-1252 decodes the ASCII range 1:1, so the <meta charset> is readable.
  const head = new TextDecoder("windows-1252")
    .decode(bytes.subarray(0, 2048))
    .toLowerCase();
  const declared = head.match(/charset\s*=\s*["']?\s*([\w-]+)/)?.[1];
  if (declared) {
    try {
      return new TextDecoder(declared).decode(bytes);
    } catch {
      /* unsupported charset label — fall through to sniffing */
    }
  }
  try {
    // Strict UTF-8: throws on invalid byte sequences (e.g. a lone 0xA0).
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

/** Detect the renderable kind from the MIME type, falling back to the filename
 *  extension (connector docs are often ``application/octet-stream``). */
function detectKind(mime: string, title: string): Kind {
  const m = (mime || "").toLowerCase();
  const ext = (title.split(".").pop() || "").toLowerCase();
  if (m.includes("pdf") || ext === "pdf") return "pdf";
  if (
    m.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "tiff"].includes(ext)
  )
    return "image";
  if (m.startsWith("video/") || ["mp4", "mov", "webm", "m4v"].includes(ext))
    return "video";
  if (m.includes("html") || ext === "html" || ext === "htm") return "html";
  if (
    m.includes("wordprocessingml") ||
    m === "application/msword" ||
    ext === "docx" ||
    ext === "doc"
  )
    return "docx";
  if (m.startsWith("text/") || ["txt", "csv", "md", "json", "log"].includes(ext))
    return "text";
  return "unsupported";
}

/**
 * Inline, view-only document renderer — NO download/print affordance, identical
 * for any origin (manual S3 bucket or source connector) and any format.
 *
 * PDFs are rasterised to <canvas> with pdf.js (no native browser PDF toolbar);
 * .docx is rendered with docx-preview; HTML in a script-disabled sandboxed
 * iframe; images via <img>; videos via <video> without its download control;
 * plain text in a <pre>, and so is a Word 97-2003 file, whose text the server
 * gives (`loadLegacyWordText`). The parent fetches the bytes
 * (carrying the auth + X-View-As-User-Id impersonation header) into a
 * same-origin blob URL, which this component reads. `select-none` + a suppressed
 * context menu remove the obvious save affordances.
 *
 * This is view-only UX, not hard DRM — bytes the browser renders can never be
 * made fully un-saveable (screenshots, devtools).
 */
export default function ViewOnlyDocument({
  blobUrl,
  mimeType,
  title,
  loadLegacyWordText,
}: ViewOnlyDocumentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<
    "loading" | "ready" | "unsupported" | "error"
  >("loading");
  const [html, setHtml] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);

  const kind = detectKind(mimeType || "", title);
  const usesContainer = kind === "pdf" || kind === "docx";

  useEffect(() => {
    if (kind === "image" || kind === "video") {
      setStatus("ready");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setHtml(null);
    setText(null);
    (async () => {
      try {
        const blob = await (await fetch(blobUrl)).blob();
        const bytes = new Uint8Array(await blob.arrayBuffer());
        if (
          (kind === "docx" || kind === "unsupported") &&
          isLegacyWord(bytes)
        ) {
          const value = loadLegacyWordText ? await loadLegacyWordText() : null;
          if (!cancelled) {
            setText(value);
            setStatus(value === null ? "unsupported" : "ready");
          }
          return;
        }
        if (kind === "unsupported") {
          if (!cancelled) setStatus("unsupported");
          return;
        }
        if (kind === "pdf") {
          const pdf = await pdfjsLib.getDocument({
            data: bytes,
            ...PDF_DOCUMENT_OPTIONS,
          }).promise;
          if (cancelled) return;
          const container = containerRef.current;
          if (!container) return;
          container.replaceChildren();
          const width = container.clientWidth || 800;
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            if (cancelled) break;
            const page = await pdf.getPage(pageNum);
            const base = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: width / base.width });
            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.className = "mb-2 w-full rounded border border-slate-200";
            const ctx = canvas.getContext("2d");
            if (!ctx) continue;
            container.appendChild(canvas);
            await page.render({ canvas, canvasContext: ctx, viewport }).promise;
          }
          if (!cancelled) setStatus("ready");
        } else if (kind === "docx") {
          if (cancelled) return;
          const container = containerRef.current;
          if (!container) return;
          container.replaceChildren();
          await renderDocx(blob, container, undefined, {
            inWrapper: true,
            breakPages: true,
          });
          if (!cancelled) setStatus("ready");
        } else if (kind === "html" || kind === "text") {
          const value = decodeTextBytes(bytes);
          if (!cancelled) {
            if (kind === "html") setHtml(value);
            else setText(value);
            setStatus("ready");
          }
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blobUrl, kind, loadLegacyWordText]);

  if (kind === "image") {
    return (
      <img
        src={blobUrl}
        alt={title}
        onContextMenu={(e) => e.preventDefault()}
        className="max-h-[70vh] w-full select-none rounded border border-slate-200 object-contain"
      />
    );
  }

  if (kind === "video") {
    return (
      <video
        src={blobUrl}
        title={title}
        controls
        controlsList="nodownload"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        className="max-h-[70vh] w-full rounded border border-slate-200 bg-slate-900"
      />
    );
  }

  return (
    <div>
      {status === "loading" && (
        <div className="flex flex-col items-center gap-3 py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-slate-500">
            <Trans>Chargement du fichier...</Trans>
          </p>
        </div>
      )}
      {status === "error" && (
        <div className="py-8 text-center">
          <FileText className="mx-auto mb-2 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            <Trans>Impossible d'afficher le fichier</Trans>
          </p>
        </div>
      )}
      {status === "unsupported" && (
        <div className="py-8 text-center">
          <FileText className="mx-auto mb-2 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            <Trans>Aperçu non disponible pour ce format de fichier.</Trans>
          </p>
        </div>
      )}
      {status === "ready" && kind === "html" && html != null && (
        <iframe
          // Script-disabled sandbox: render the markup, run nothing.
          sandbox="allow-same-origin"
          srcDoc={html}
          title={title}
          className="w-full rounded border border-slate-200"
          style={{ height: "70vh" }}
        />
      )}
      {status === "ready" && text != null && (
        <pre className="max-h-[70vh] select-none overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          {text}
        </pre>
      )}
      {/* PDF canvases + docx render into this container. */}
      <div
        ref={containerRef}
        onContextMenu={(e) => e.preventDefault()}
        className={
          usesContainer &&
          text === null &&
          (status === "loading" || status === "ready")
            ? "max-h-[70vh] select-none overflow-y-auto"
            : "hidden"
        }
      />
    </div>
  );
}
